import { useEffect, lazy, Suspense } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import Home from "./pages/Home";
import AppLayout from "./components/layout/AppLayout";
import AuthGuard from "./components/common/AuthGuard";
import { trackPageView, initAnalyticsTracking } from "./services/analyticsService";

const About = lazy(() => import("./pages/About"));
const Services = lazy(() => import("./pages/Services"));
const Fleet = lazy(() => import("./pages/Fleet"));
const Booking = lazy(() => import("./pages/Booking"));
const Contact = lazy(() => import("./pages/Contact"));
const Faq = lazy(() => import("./pages/Faq"));
const RoutesIndex = lazy(() => import("./pages/RoutesIndexPage"));
const RouteLanding = lazy(() => import("./pages/RouteLandingPage"));
const AdminLogin = lazy(() => import("./modules/admin/pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./modules/admin/pages/AdminDashboardPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const seoMetaMap = {
  "/": {
    title: "Trishul Tours & Travels | Taxi & Outstation Cabs",
    description: "Book one-way and round-trip cabs with Trishul Tours & Travels. Explore intercity taxi options, airport transfers and outstation trips. Request your fare on WhatsApp.",
  },
  "/about": {
    title: "About Us | Trishul Tours & Travels - Vapi & Sarigam Cab Service",
    description: "Learn about Trishul Tours & Travels based in Vapi & Sarigam. Safety, punctuality, clean vehicles, and 5.0★ Google rated service.",
  },
  "/services": {
    title: "Services | Taxi Rental, Outstation Cabs & Airport Transfers - Trishul Travels",
    description: "Explore outstation round-trip cabs, one-way taxi rentals, 24/7 airport transfers, local hourly packages, and group mini buses with drivers.",
  },
  "/fleet": {
    title: "Our Fleet | Swift Dzire, Innova Crysta, Ertiga, XUV700 & Traveller - Trishul Travels",
    description: "View our clean, well-maintained fleet of Sedans, MUVs, Luxury SUVs, and 17-Seater Force Travellers available for rental in Vapi, Daman, Silvassa.",
  },
  "/booking": {
    title: "Book Your Vehicle | Instant Online Cab & Rental Request - Trishul Travels",
    description: "Reserve your vehicle online in under 2 minutes. Instant booking requests for one-way, round-trip outstation cabs and airport transfers.",
  },
  "/contact": {
    title: "Contact Us | Trishul Tours & Travels - 24/7 Helpline +91 83203 42657",
    description: "Get in touch with Trishul Tours & Travels in Sarigam / Vapi. Call or WhatsApp +91 83203 42657 for quick quotes and 24/7 support.",
  },
  "/faqs": {
    title: "FAQs | Common Questions About Car Rental & Cab Booking - Trishul Travels",
    description: "Find answers to common questions about booking, documents required, advance payments, and outstation taxi policies.",
  },
};

function SeoRouteHandler() {
  const location = useLocation();

  useEffect(() => {
    initAnalyticsTracking();
    trackPageView(location.pathname);

    // If the path starts with /routes, let the route component handle its own custom SEO metadata
    if (location.pathname.startsWith("/routes")) {
      window.scrollTo(0, 0);
      return;
    }

    const meta = seoMetaMap[location.pathname] || seoMetaMap["/"];
    document.title = meta.title;

    let descEl = document.querySelector('meta[name="description"]');
    if (descEl) {
      descEl.setAttribute("content", meta.description);
    }

    let canonicalEl = document.querySelector('link[rel="canonical"]');
    if (canonicalEl) {
      canonicalEl.setAttribute("href", `https://trishultoursandtravel.com${location.pathname === "/" ? "/" : location.pathname}`);
    }

    window.scrollTo(0, 0);
  }, [location]);

  return null;
}

export default function App() {
  return (
    <AppLayout>
      <SeoRouteHandler />
      <Suspense
        fallback={
          <div
            style={{
              minHeight: "45vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--muted)",
              fontSize: "0.92rem",
            }}
          >
            Loading...
          </div>
        }
      >
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/fleet" element={<Fleet />} />
          <Route path="/booking" element={<Booking />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faqs" element={<Faq />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/routes" element={<RoutesIndex />} />
          <Route path="/routes/:slug" element={<RouteLanding />} />
          <Route path="/customer/*" element={<Navigate to="/" replace />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin/dashboard"
            element={
              <AuthGuard role="admin">
                <AdminDashboard />
              </AuthGuard>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </AppLayout>
  );
}
